import React, { Fragment, useContext, useMemo, useRef } from "react";
import { Loader, useAPI, ViewContext } from "components/lib";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import Axios from "axios";
import "survey-core/defaultV2.min.css";
import { buildFyiSettingsPayload, fyiPdfFormValues } from "./fyi-settings-payload";
import { applyStackgoTheme } from "components/survey/stackgo-theme";

StylesManager.applyTheme("defaultV2");

const env = process.env.NODE_ENV;
const navigateToUrl =
  env === "production"
    ? `https://identity.stackgo.io/account`
    : `http://localhost:3000/account`;

// A fresh copy per call: SurveyJS is handed this object, and one page's model
// must never share it with the next.
export function buildFyiSettingsJson() {
  return {
    completedHtml: "<h3>Your changes have been saved.</h3>",
    showQuestionNumbers: "off",
    pages: [
      {
        name: "FYI Settings",
        elements: [
          {
            type: "boolean",
            name: "enabled",
            title: "Enabled",
            isRequired: true,
            defaultValue: false,
          },
          {
            type: "text",
            name: "fyi_access_key_id",
            title: "FYI Access Key ID",
            isRequired: true,
          },
          {
            type: "text",
            name: "fyi_region",
            title: "FYI Region (e.g. ap-southeast-2 (AU), eu-west-2 (UK))",
            isRequired: true,
          },
          {
            type: "text",
            inputType: "password",
            name: "fyi_secret_key_id",
            title: "FYI Secret Key ID",
            isRequired: true,
          },
          {
            type: "boolean",
            name: "pdf_results_writeback",
            title: "PDF Results Writeback",
            defaultValue: false,
          },
          // FYI PDF writeback options (R1): the dashboard's Download PDF
          // toggles, with its labels and defaults. Internal names say
          // `sanctions`; the label says PEP/Sanctions. Flat question names —
          // buildFyiSettingsPayload builds pdf_writeback_options from them,
          // and only while the toggle is on, since SurveyJS clears these
          // hidden questions before save.
          {
            type: "boolean",
            name: "pdf_include_images",
            title: "Include images",
            visibleIf: "{pdf_results_writeback} = true",
            defaultValue: true,
          },
          {
            type: "boolean",
            name: "pdf_include_sanctions",
            title: "Include PEP/Sanctions",
            visibleIf: "{pdf_results_writeback} = true",
            defaultValue: false,
          },
          {
            type: "boolean",
            name: "pdf_include_adverse_media",
            title: "Include adverse media",
            visibleIf: "{pdf_results_writeback} = true",
            defaultValue: false,
          },
          {
            type: "text",
            name: "cabinet_id",
            title: "Cabinet ID",
            isRequired: false,
            visibleIf: "{pdf_results_writeback} = true",
            inputType: "number"
          },
          {
            type: "boolean",
            name: "tranche2_enabled",
            title: "Enable Tranche 2 (AML) triggers & writebacks",
            description:
              "Turn on AML (Tranche 2) verifications for your FYI account. Once enabled, ticking an entity's 'AML Command' field starts a verification, and the risk-assessment results are written back to that entity. Your existing KYC fields and triggers are unchanged. Create the FYI custom fields shown below first — our team will map them for you.",
            defaultValue: false,
          },
          {
            type: "html",
            name: "tranche2_fields_help",
            visibleIf: "{tranche2_enabled} = true",
            html:
              "<strong>Tranche 2 (AML) — FYI custom fields to create</strong>" +
              "<br /><br />" +
              "Before this can go live, create the following <strong>entity custom fields</strong> in FYI (Settings &rarr; Custom Fields). Once they exist, our team maps each one to IdentityCheck for you." +
              "<ul style=\"margin-top:8px;\">" +
              "<li><strong>AML Command</strong> &mdash; <em>Checkbox / Yes-No</em>: tick this on an entity to start an AML (Tranche 2) verification.</li>" +
              "<li><strong>AML Status</strong> &mdash; <em>List (single select)</em> with the options: Started, Awaiting risk assessment, In Progress, Completed, Failed, Excluded. Set an entity to <strong>Excluded</strong> to stop it from ever triggering.</li>" +
              "<li><strong>Risk Assessment Completed Date</strong> &mdash; <em>Date</em>.</li>" +
              "<li><strong>RA Completed By</strong> &mdash; <em>Text</em>.</li>" +
              "<li><strong>Risk Rating</strong> &mdash; <em>Text</em>.</li>" +
              "<li><strong>Risk Assessment Link</strong> &mdash; <em>Text</em> (stores a link to the completed risk assessment).</li>" +
              "</ul>",
          }
        ],
      },
    ],
  };
}

// The page's survey, hydrated from the stored FYI row (null for a firm that
// has not connected FYI yet), posting its payload through onSave.
export function createFyiSettingsSurvey({ settings, navigateToUrl, onSave }) {
  const json = buildFyiSettingsJson();
  if (navigateToUrl) json.navigateToUrl = navigateToUrl;

  const survey = new Model(json);
  applyStackgoTheme(survey);

  if (settings) {
    survey.setValue("enabled", settings?.enabled);
    survey.setValue("tranche2_enabled", settings?.data?.tranche2?.enabled === true);
    survey.setValue("fyi_access_key_id", settings?.data?.fyi_access_key_id);
    survey.setValue("fyi_secret_key_id", settings?.data?.fyi_secret_key_id);
    survey.setValue("fyi_region", settings?.data?.fyi_region);
    survey.setValue("cabinet_id", settings?.data?.cabinet_id);
  }
  // The PDF switch and its options always take a real boolean, falling back
  // to the dashboard defaults, so a fresh form shows images on.
  const pdfValues = fyiPdfFormValues(settings?.data);
  Object.keys(pdfValues).forEach((name) => survey.setValue(name, pdfValues[name]));

  // SurveyJS redirects straight after onComplete unless a save is marked in
  // progress, which would hide a failed POST behind the redirect. So the
  // redirect waits for onSave's promise, and a failure shows SurveyJS's error
  // toast, whose Try again posts the same answers again.
  survey.onComplete.add((sender, options) => {
    options.showSaveInProgress();
    Promise.resolve(onSave(buildFyiSettingsPayload(sender.data))).then(
      () => options.showSaveSuccess(),
      () => options.showSaveError()
    );
  });
  return survey;
}

async function saveSurveyResults(url, json) {
  await Axios.post(url, json);
}

export function FYISettingsView(props) {
  // wrapped in useRef, as useAPI does, so a re-render of the view (a
  // notification, say) never rebuilds the survey below
  const context = useRef(useContext(ViewContext));
  const existingSettingsResponse = useAPI("/api/integration/fyi");
  // getFYISettings answers { data: {} } when there is no FYI row yet, so data
  // is truthy once the GET resolves, row or not.
  const rows = existingSettingsResponse.data;

  // Built once, from the stored settings, and never again on a re-render —
  // a fresh model would drop what the admin has typed. Above the Loader
  // guard, so the hook runs on every render.
  const survey = useMemo(() => {
    if (!rows) return null;
    return createFyiSettingsSurvey({
      settings: Array.isArray(rows) ? rows[0] || null : null,
      navigateToUrl,
      onSave: (payload) =>
        saveSurveyResults("/api/integration/fyi/create", payload).catch((err) => {
          context.current?.handleError?.(err);
          throw err;
        }),
    });
  }, [rows]);

  // Don't render a form we haven't hydrated. useAPI never clears `loading` when
  // the GET throws, so a form shown then would sit at its defaults and the next
  // save would write them over the firm's stored settings. Same Loader guard as
  // the HubSpot view.
  if (!survey) return <Loader />;

  return (
    <Fragment>
      <Survey model={survey} />
    </Fragment>
  );
}
